import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { useStore, type CategoryFilter } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { Segmented } from "@/components/atoms/Segmented";
import { MarketCard } from "@/components/market/MarketCard";
import { MarketTable } from "@/components/market/MarketTable";
import { HeroStrip } from "@/components/market/HeroStrip";
import type { components } from "@/api/schema.gen";

type MarketView = components["schemas"]["MarketView"];

const CATEGORIES: { value: CategoryFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "crypto", label: "Crypto" },
  { value: "politics", label: "Politics" },
  { value: "sports", label: "Sports" },
  { value: "tech", label: "Tech" },
  { value: "science", label: "Science" },
  { value: "culture", label: "Culture" },
];

export function MarketsView() {
  const category = useStore((s) => s.category);
  const setCategory = useStore((s) => s.setCategory);
  const layout = useStore((s) => s.layout);
  const setLayout = useStore((s) => s.setLayout);
  const [markets, setMarkets] = useState<MarketView[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchMarkets = async () => {
      const { data, error } = await api.GET("/v1/markets", {
        params: {
          query: {
            category: category === "all" ? undefined : category,
            status: "open",
            limit: 30,
          },
        },
      });
      if (cancelled) return;
      if (data) {
        setMarkets(data.items);
        setNextCursor(data.next_cursor ?? null);
      } else if (error) {
        console.error(error);
        setMarkets([]);
      }
      setLoading(false);
    };
    fetchMarkets();
    return () => {
      cancelled = true;
    };
  }, [category]);

  const featured = markets.slice(0, 3);

  return (
    <div className="flex flex-col gap-6 px-4 pb-28 pt-4 sm:px-6 lg:pb-20">
      {featured.length > 0 && <HeroStrip markets={featured} />}

      <div className="flex flex-wrap items-center gap-2">
        {/* I margini negativi fanno scorrere i chip da bordo a bordo su mobile
            invece di fermarsi al padding della pagina. */}
        <div className="scroll-chips -mx-4 w-[calc(100%+2rem)] px-4 sm:mx-0 sm:w-auto sm:flex-1 sm:px-0">
          <Segmented
            options={CATEGORIES}
            value={category}
            onChange={setCategory}
          />
        </div>
        <Segmented
          options={[
            { value: "grid" as const, label: "Grid" },
            { value: "list" as const, label: "List" },
          ]}
          value={layout}
          onChange={setLayout}
        />
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : markets.length === 0 ? (
        <EmptyState />
      ) : layout === "grid" ? (
        <div className="grid gap-[14px]" style={{
          // `min(320px, 100%)`: sotto i 320px utili la colonna si adatta invece
          // di sforare il viewport.
          gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
        }}>
          {markets.map((m, i) => (
            <MarketCard key={m.id} market={m} index={i} />
          ))}
        </div>
      ) : (
        <MarketTable markets={markets} />
      )}

      {nextCursor && !loading && (
        <div className="flex justify-center">
          <button className="btn">Load more</button>
        </div>
      )}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid gap-[14px]" style={{
          // `min(320px, 100%)`: sotto i 320px utili la colonna si adatta invece
          // di sforare il viewport.
          gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
        }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="card h-[260px] p-4">
          <div className="skel h-3 w-1/3" />
          <div className="skel mt-3 h-5 w-3/4" />
          <div className="skel mt-2 h-5 w-2/3" />
          <div className="skel mt-6 h-20 w-full" />
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="skel h-10" />
            <div className="skel h-10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card flex flex-col items-center gap-3 py-16 text-center">
      <Icon.Sparkles size={28} className="text-muted" />
      <h3 className="text-[15px] font-semibold">No markets yet</h3>
      <p className="max-w-md text-[13px] text-muted">
        Quando un admin pubblicherà markets via POST /v1/admin/markets, appariranno qui.
        Lancia il backend (<span className="font-mono">just api-run</span>) e crea il primo
        market dalla console.
      </p>
    </div>
  );
}
